import { useState, useEffect } from 'react'
import {
  FileText, Check, X, AlertTriangle, Download, Search, Printer,
  Clock, CheckCircle, XCircle, ChevronRight, ArrowLeft, Package,
  Settings, Layers, Scissors, BookOpen, Warehouse, RotateCcw
} from 'lucide-react'
import { spkService } from '../services/spkService'
import { fileService } from '../services/fileService'
import { masterService } from '../services/masterService'
import { operatorService } from '../services/operatorService'
import { useAuth } from '../context/AuthContext'

function StationUpdatePage() {
  const { user } = useAuth()
  const [spks, setSpks] = useState([])
  const [vendors, setVendors] = useState([])
  const [racks, setRacks] = useState([])
  const [operators, setOperators] = useState([])
  const [selectedSPK, setSelectedSPK] = useState(null)
  const [loading, setLoading] = useState(true)

  // Modal states
  const [activeModal, setActiveModal] = useState(null)
  const [modalData, setModalData] = useState(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [spkRes, vendorRes, rackRes, operatorRes] = await Promise.all([
        spkService.getAll({ limit: 500, page: 1 }),
        masterService.getVendors().catch(() => ({ success: true, data: { vendors: [] } })),
        masterService.getRacks().catch(() => ({ success: true, data: { racks: [] } })),
        operatorService.getAll({ status: 'active' }).catch(() => ({ success: true, data: { operators: [] } }))
      ])
      if (spkRes.success) setSpks(spkRes.data.spks || [])
      if (vendorRes.success) setVendors(vendorRes.data.vendors || [])
      if (rackRes.success) setRacks(rackRes.data.racks || [])
      if (operatorRes.success) setOperators(operatorRes.data.operators || [])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const refreshSelectedSPK = async () => {
    if (!selectedSPK) return
    try {
      const response = await spkService.getById(selectedSPK.id)
      if (response.success) {
        setSelectedSPK(response.data.spk)
        setSpks(prev => prev.map(s => s.id === response.data.spk.id ? response.data.spk : s))
      }
    } catch (error) {
      console.error('Error refreshing SPK:', error)
    }
  }

  const openModal = (type, data = null) => {
    setActiveModal(type)
    setModalData(data)
  }

  const closeModal = () => {
    setActiveModal(null)
    setModalData(null)
  }

  const handleModalSuccess = () => {
    closeModal()
    refreshSelectedSPK()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        {selectedSPK ? (
          <button onClick={() => setSelectedSPK(null)} className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Kembali ke Daftar</span>
          </button>
        ) : (
          <h1 className="text-xl font-bold text-gray-800">Update Stasiun</h1>
        )}
        <span className="text-sm text-gray-500">{spks.length} SPK</span>
      </div>

      {/* Content */}
      {selectedSPK ? (
        <SPKStationDetail
          spk={selectedSPK}
          vendors={vendors}
          onOpenModal={openModal}
          user={user}
        />
      ) : (
        <SPKCardList spks={spks} onSelect={setSelectedSPK} />
      )}

      {/* All Modals */}
      {activeModal === 'validation' && (
        <ValidationModal 
          spk={selectedSPK} 
          onClose={closeModal} 
          onSuccess={handleModalSuccess} 
          onRefresh={refreshSelectedSPK}
        />
      )}
      {activeModal === 'infoCetak' && modalData && (
        <InfoCetakModal spk={selectedSPK} track={modalData} vendors={vendors} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'qcCetak' && modalData && (
        <QCCetakModal spk={selectedSPK} track={modalData} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'peruntukan' && (
        <PeruntukanModal spk={selectedSPK} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'qcCover' && modalData && (
        <QCCoverModal spk={selectedSPK} track={modalData} vendors={vendors} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'laminasi' && modalData && (
        <LaminasiModal spk={selectedSPK} track={modalData} operators={operators.filter(o => o.station === 'laminasi')} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'qcBinding' && modalData && (
        <QCBindingModal spk={selectedSPK} batch={modalData} operators={operators.filter(o => o.station === 'binding')} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'qcPotong' && modalData && (
        <QCPotongModal spk={selectedSPK} batch={modalData} operators={operators.filter(o => o.station === 'potong')} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'gudang' && modalData && (
        <GudangModal spk={selectedSPK} batch={modalData} racks={racks} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'editBatchSusun' && modalData && (
        <EditBatchSusunModal spk={selectedSPK} batch={modalData} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'lanjutCustom' && modalData && (
        <LanjutCustomModal spk={selectedSPK} data={modalData} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'rollback' && modalData && (
        <RollbackModal spk={selectedSPK} data={modalData} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'rollbackBatch' && modalData && (
        <RollbackBatchModal spk={selectedSPK} batch={modalData} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
      {activeModal === 'editPrintInfo' && modalData && (
        <EditPrintInfoModal spk={selectedSPK} printInfo={modalData} vendors={vendors} onClose={closeModal} onSuccess={handleModalSuccess} />
      )}
    </div>
  )
}

// ============================================
// SPK CARD LIST
// ============================================
function SPKCardList({ spks, onSelect }) {
  const getPriorityColor = (p) => p === 'urgent' ? 'bg-red-500 text-white' : p === 'high' ? 'bg-orange-500 text-white' : 'bg-blue-500 text-white'
  const getStationLabel = (s) => ({ 'validasi': 'Validasi', 'cetak_isi': 'Cetak', 'potong_isi': 'Potong', 'susun': 'Susun', 'cetak_cover': 'Cetak', 'qc_cover': 'QC', 'laminasi': 'Laminasi', 'done': 'Selesai' }[s] || s)

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {spks.map(spk => (
        <div key={spk.id} onClick={() => onSelect(spk)} className="bg-white rounded-xl shadow-sm border hover:shadow-md hover:border-blue-200 transition-all cursor-pointer active:scale-[0.98]">
          <div className="p-3 border-b">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-bold text-gray-800 truncate text-sm">{spk.judul}</h3>
                <p className="text-xs text-gray-500">{spk.id}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-bold ${getPriorityColor(spk.priority)}`}>{spk.priority?.toUpperCase()}</span>
            </div>
            <div className="flex gap-2 mt-2 text-xs text-gray-600">
              <span>{spk.qty} eks</span>
              <span>•</span>
              <span>{spk.jenjang}</span>
              {(spk.customSchools?.length > 0) && <><span>•</span><span className="text-yellow-600">{spk.customSchools.length} custom</span></>}
            </div>
          </div>
          <div className="p-3 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">📄 ISI: {getStationLabel(spk.station_isi)}</span>
              <span className="text-gray-600">📄 COVER: {getStationLabel(spk.station_cover)}</span>
            </div>
            {(spk.printInfo?.length > 0) && (
              <div className="flex flex-wrap gap-1">
                {spk.printInfo.filter(pi => !pi.school_code).map((pi, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                    {pi.track_type === 'isi' ? 'ISI' : 'COVER'}:{' '}
                    {pi.vendor_type === 'internal'
                      ? (pi.print_mode ? pi.print_mode.toUpperCase() : 'Internal')
                      : (pi.vendor_name || 'Makloon')}
                  </span>
                ))}
              </div>
            )}
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-green-500" style={{ width: `${Math.round(((spk.progress_isi || 0) + (spk.progress_cover || 0)) / 2)}%` }}></div>
            </div>
          </div>
          <div className="px-3 py-2 bg-gray-50 flex justify-between text-xs text-gray-500">
            <span>DL: {new Date(spk.deadline).toLocaleDateString('id-ID')}</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      ))}
      {spks.length === 0 && <div className="col-span-full text-center py-12 text-gray-500"><FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>Tidak ada SPK</p></div>}
    </div>
  )
}

// ============================================
// SPK STATION DETAIL
// ============================================
function SPKStationDetail({ spk, vendors, onOpenModal, user }) {
  const isAdmin = user?.permissions?.includes('all');
  const customSchools = spk.customSchools || []
  const batches = spk.batches || []
  const files = spk.files || []

  const getFileStatus = (type, schoolCode = null) => files.find(f => f.file_type === type && (schoolCode ? f.school_code === schoolCode : !f.school_code))?.status
  const isValidated = (type, schoolCode) => getFileStatus(type, schoolCode) === 'validated'
  
  const allFilesValidated = isValidated('isi') && isValidated('cover') && customSchools.every(cs => {
    const cValid = isValidated('cover_custom', cs.school_code)
    return cs.custom_type === 'cover_sisipan' ? cValid && isValidated('sisipan', cs.school_code) : cValid
  })

  // Station definitions
  const stationsIsi = [{ id: 'validasi', n: 1 }, { id: 'cetak_isi', n: 2 }, { id: 'potong_isi', n: 3 }, { id: 'susun', n: 4 }]
  const stationsCover = [{ id: 'cetak_cover', n: 1 }, { id: 'qc_cover', n: 2 }, { id: 'laminasi', n: 3 }]
  const stationsSisipan = [{ id: 'cetak_sisipan', n: 1 }, { id: 'qc_sisipan', n: 2 }]
  const stationsMain = [{ id: 'binding', n: 1 }, { id: 'potong_sisir', n: 2 }, { id: 'qc_potong', n: 3 }, { id: 'gudang', n: 4 }]

  // Check if cover is done for binding
  const isCoverDone = spk.station_cover === 'done'
  const getCustomCoverDone = (code) => customSchools.find(c => c.school_code === code)?.station_cover === 'done'
  const getCustomSisipanDone = (code) => {
    const cs = customSchools.find(c => c.school_code === code)
    return cs?.custom_type === 'cover_sisipan' ? cs.station_sisipan === 'done' : true
  }

  // Calculate custom qty
  const customTotalQty = customSchools.reduce((s, c) => s + (c.qty || 0), 0)
  const regulerQty = spk.qty - customTotalQty

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-3">
        <div className="flex justify-between items-start gap-2">
          <div>
            <h2 className="text-lg font-bold">{spk.judul}</h2>
            <p className="text-xs text-gray-500">{spk.id} • {spk.qty} eks • Deadline: {new Date(spk.deadline).toLocaleDateString('id-ID')}</p>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-bold ${spk.priority === 'urgent' ? 'bg-red-100 text-red-700' : spk.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>{spk.priority?.toUpperCase()}</span>
        </div>
        
        {/* Progress tracks - horizontal scroll */}
        <div className="mt-3 overflow-x-auto pb-2 -mx-3 px-3">
          <div className="flex gap-2 min-w-max">
            <TrackBadge label="ISI" stations={stationsIsi} current={spk.station_isi} progress={spk.progress_isi} color="blue" printInfo={(spk.printInfo || []).find(pi => pi.track_type === 'isi' && !pi.school_code)} />
            <TrackBadge label="COVER" stations={stationsCover} current={spk.station_cover} progress={spk.progress_cover} color="red" printInfo={(spk.printInfo || []).find(pi => pi.track_type === 'cover' && !pi.school_code)} />
            {customSchools.map(cs => <TrackBadge key={`c-${cs.school_code}`} label={`CVR ${cs.school_code}`} stations={stationsCover} current={cs.station_cover} progress={cs.progress_cover} color="yellow" printInfo={(spk.printInfo || []).find(pi => pi.track_type === 'cover_custom' && pi.school_code === cs.school_code)} />)}
            {customSchools.filter(c => c.custom_type === 'cover_sisipan').map(cs => <TrackBadge key={`s-${cs.school_code}`} label={`SIS ${cs.school_code}`} stations={stationsSisipan} current={cs.station_sisipan} progress={cs.progress_sisipan} color="purple" printInfo={(spk.printInfo || []).find(pi => pi.track_type === 'sisipan' && pi.school_code === cs.school_code)} />)}
            {customSchools.length > 0 ? customSchools.map(cs => <TrackBadge key={`m-${cs.school_code}`} label={`MAIN ${cs.school_code}`} stations={stationsMain} current={cs.station_main || 'binding'} progress={cs.progress_main || 0} color="gray" />) : <TrackBadge label="MAIN" stations={stationsMain} current={spk.station_main || 'binding'} progress={spk.progress_main || 0} color="gray" />}
          </div>
        </div>

        {/* Rollback button - admin only */}
        {isAdmin && (
          <div className="mt-3 pt-3 border-t">
            <button
              onClick={() => onOpenModal('rollback', { spk, customSchools })}
              className="flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-800 hover:bg-orange-50 px-2 py-1.5 rounded-lg transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Kembalikan ke Stasiun Sebelumnya
            </button>
          </div>
        )}
      </div>

      {/* Batches - Custom school batches */}
      {batches.filter(b => b.batch_type === 'custom').map(batch => (
        <BatchCard
          key={batch.id}
          batch={batch}
          spk={spk}
          customSchools={customSchools}
          isCoverDone={getCustomCoverDone(batch.school_code) && getCustomSisipanDone(batch.school_code)}
          onOpenModal={onOpenModal}
          isAdmin={isAdmin}
        />
      ))}

      {/* Batches - Reguler batches */}
      {batches.filter(b => b.batch_type === 'reguler').map(batch => (
        <BatchCard
          key={batch.id}
          batch={batch}
          spk={spk}
          customSchools={customSchools}
          isCoverDone={isCoverDone}
          onOpenModal={onOpenModal}
          isAdmin={isAdmin}
        />
      ))}

      {/* ISI Track Card */}
      <StationActionCard
        track="ISI"
        station={spk.station_isi}
        isValidation={spk.station_isi === 'validasi'}
        validated={isValidated('isi')}
        allFilesValidated={allFilesValidated}
        color="green"
        customSchools={customSchools}
        batches={batches}
        spk={spk}
        onOpenModal={onOpenModal}
      />

      {/* COVER Reguler */}
      {spk.station_cover !== 'done' && (
        <StationActionCard
          track="COVER (Reguler)"
          trackType="cover"
          station={spk.station_cover}
          validated={isValidated('cover')}
          color="red"
          qty={regulerQty}
          customSchools={customSchools}
          spk={spk}
          onOpenModal={onOpenModal}
        />
      )}

      {/* Custom Schools */}
      {customSchools.map(cs => (
        <div key={cs.school_code} className="space-y-3">
          {cs.station_cover !== 'done' && (
            <StationActionCard
              track={`COVER (${cs.school_code})`}
              trackType="cover_custom"
              schoolCode={cs.school_code}
              station={cs.station_cover}
              validated={isValidated('cover_custom', cs.school_code)}
              color="yellow"
              qty={cs.qty}
              spk={spk}
              onOpenModal={onOpenModal}
            />
          )}
          {cs.custom_type === 'cover_sisipan' && cs.station_sisipan !== 'done' && (
            <StationActionCard
              track={`SISIPAN (${cs.school_code})`}
              trackType="sisipan"
              schoolCode={cs.school_code}
              station={cs.station_sisipan}
              validated={isValidated('sisipan', cs.school_code)}
              color="purple"
              qty={cs.qty}
              spk={spk}
              onOpenModal={onOpenModal}
            />
          )}
        </div>
      ))}
    </div>
  )
}

// ============================================
// TRACK BADGE
// ============================================
function TrackBadge({ label, stations, current, progress, color, printInfo }) {
  const colors = { blue: 'bg-blue-100 text-blue-600', red: 'bg-red-100 text-red-600', yellow: 'bg-yellow-100 text-yellow-600', purple: 'bg-purple-100 text-purple-600', gray: 'bg-gray-100 text-gray-600' }
  const bgColors = { blue: 'bg-blue-500', red: 'bg-red-500', yellow: 'bg-yellow-500', purple: 'bg-purple-500', gray: 'bg-gray-500' }
  const idx = current === 'done' ? stations.length : stations.findIndex(s => s.id === current)

  return (
    <div className={`p-2 rounded-lg ${colors[color]} min-w-[90px]`}>
      <div className="text-[10px] font-semibold truncate mb-1">{label}</div>
      <div className="flex items-center gap-0.5">
        {stations.map((s, i) => (
          <div key={s.id} className="flex items-center">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold ${i <= idx ? `${bgColors[color]} text-white` : 'bg-white text-gray-400 border'}`}>
              {i < idx ? <Check className="w-2 h-2" /> : s.n}
            </div>
            {i < stations.length - 1 && <div className={`w-1 h-0.5 ${i < idx ? bgColors[color] : 'bg-gray-300'}`} />}
          </div>
        ))}
        {current === 'done' && <span className="ml-1 text-[10px]">✓</span>}
        {current !== 'done' && <span className="ml-1 text-[10px]">{progress || 0}%</span>}
      </div>
      {printInfo && (
        <div className="mt-1 text-[9px] font-medium truncate">
          {printInfo.vendor_type === 'internal'
            ? printInfo.print_mode
              ? `🏭 ${printInfo.print_mode === 'offset' ? 'OFFSET' : 'COMCOLOR'}`
              : '🏭 Internal'
            : `🏭 ${printInfo.vendor_name || 'Makloon'}`}
        </div>
      )}
    </div>
  )
}

// ============================================
// BATCH CARD
// ============================================
function BatchCard({ batch, spk, customSchools, isCoverDone, onOpenModal, isAdmin }) {
  const isCustom = batch.batch_type === 'custom'
  const cs = isCustom ? customSchools.find(c => c.school_code === batch.school_code) : null
  const label = isCustom ? `${batch.school_code} ${batch.batch_number}` : `BATCH ${batch.batch_number} (${batch.qty} eks)`

  const isBlocked = !isCoverDone
  const blockReason = isCustom
    ? `Tunggu Cover ${cs?.custom_type === 'cover_sisipan' ? '& Sisipan ' : ''}${batch.school_code} selesai`
    : 'Tunggu Cover Reguler selesai'

  // QC Data
  const qcSusun = batch.qc_data?.susun || null
  const qcBinding = batch.qc_data?.binding || null
  const qcPotong = batch.qc_data?.potong || null

  // Color scheme for batch stages:
  // - Binding: Emerald (bg-emerald-600)
  // - Potong Sisir: Cyan (bg-cyan-600)
  // - Input Gudang: Purple (bg-purple-600)
  const getButtonConfig = () => {
    if (isBlocked) return null
    switch (batch.station) {
      case 'binding': return { label: 'QC Binding & Lanjut', modal: 'qcBinding', color: 'bg-emerald-600' }
      case 'potong_sisir': return { label: 'QC Potong', modal: 'qcPotong', color: 'bg-cyan-600' }
      case 'qc_potong': return { label: 'Input Gudang', modal: 'gudang', color: 'bg-purple-600' }
      default: return null
    }
  }
  const btn = getButtonConfig()

  // Badge color based on station
  const getStationBadge = () => {
    switch (batch.station) {
      case 'binding': return { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'BINDING' }
      case 'potong_sisir': return { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'POTONG' }
      case 'qc_potong': return { bg: 'bg-purple-100', text: 'text-purple-700', label: 'SIAP GUDANG' }
      case 'gudang': return { bg: 'bg-green-100', text: 'text-green-700', label: 'DI GUDANG' }
      default: return { bg: 'bg-gray-100', text: 'text-gray-700', label: batch.station?.toUpperCase() }
    }
  }
  const stationBadge = getStationBadge()

  if (batch.station === 'gudang') return null // Already in warehouse

  return (
    <div className={`bg-white rounded-xl shadow-sm border-l-4 ${isCustom ? 'border-l-yellow-500' : 'border-l-blue-500'} p-3`}>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs ${isCustom ? 'bg-yellow-500' : 'bg-blue-500'}`}>
            {isCustom ? '🏫' : '📦'}
          </span>
          <span className="font-semibold text-sm">{label}</span>
          <span className={`px-1.5 py-0.5 text-[10px] rounded ${stationBadge.bg} ${stationBadge.text}`}>{stationBadge.label}</span>
        </div>
        <div className="flex gap-1">
          <span className="px-2 py-0.5 bg-gray-100 text-xs rounded">{isCustom ? 'CUSTOM' : 'BATCH'}</span>
          {isBlocked && <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded">BLOCKED</span>}
        </div>
      </div>

      {/* Riwayat QC - Selalu tampil jika ada data */}
      {(qcSusun || qcBinding || qcPotong) && (
        <div className="mb-2 space-y-1">
          <p className="text-[10px] font-medium text-gray-500 uppercase">Riwayat QC:</p>
          {qcSusun && (
            <QCHistoryItem
              label="Susun"
              data={qcSusun}
              color="amber"
              onEdit={() => onOpenModal('editBatchSusun', batch)}
            />
          )}
          {qcBinding && (
            <QCHistoryItem
              label="Binding"
              data={qcBinding}
              color="emerald"
              onEdit={() => onOpenModal('qcBinding', batch)}
            />
          )}
          {qcPotong && (
            <QCHistoryItem
              label="Potong"
              data={qcPotong}
              color="cyan"
              onEdit={() => onOpenModal('qcPotong', batch)}
            />
          )}
        </div>
      )}

      {isBlocked && (
        <div className="flex items-center gap-2 text-yellow-700 text-xs mb-2 bg-yellow-50 p-2 rounded">
          <AlertTriangle className="w-3 h-3" />
          {blockReason}
        </div>
      )}

      <div className="flex gap-2">
        <div className="flex-1">
          {isBlocked ? (
            <div className="w-full py-2 bg-gray-100 rounded text-center text-gray-500 text-sm">
              <Clock className="w-4 h-4 inline mr-1" /> Menunggu
            </div>
          ) : btn ? (
            <button onClick={() => onOpenModal(btn.modal, batch)} className={`w-full py-2 ${btn.color} text-white rounded text-sm font-medium hover:opacity-90`}>
              <Settings className="w-4 h-4 inline mr-1" /> {btn.label}
            </button>
          ) : null}
        </div>
        {isAdmin && batch.station !== 'binding' && (
          <button
            onClick={() => onOpenModal('rollbackBatch', batch)}
            className="px-3 py-2 border border-orange-300 text-orange-600 rounded text-sm hover:bg-orange-50 transition-colors"
            title="Kembalikan stasiun"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================
// QC HISTORY ITEM (untuk menampilkan riwayat QC di BatchCard)
// ============================================
function QCHistoryItem({ label, data, color, onEdit }) {
  const colorMap = {
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    cyan: 'bg-cyan-50 border-cyan-200 text-cyan-700'
  }

  return (
    <div className={`flex items-center justify-between p-1.5 rounded border text-[10px] ${colorMap[color]}`}>
      <div className="flex items-center gap-2">
        <span className="font-medium">{label}:</span>
        <span>Awal: {data.jumlahAwal}</span>
        {data.jumlahReject > 0 && <span className="text-red-500">Reject: {data.jumlahReject}</span>}
        <span>Akhir: {data.jumlahAkhir}</span>
        {data.operatorName && <span className="text-gray-500">({data.operatorName})</span>}
      </div>
      <button
        onClick={onEdit}
        className="px-1.5 py-0.5 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-[9px]"
      >
        Edit
      </button>
    </div>
  )
}

// ============================================
// STATION ACTION CARD
// ============================================
function StationActionCard({ track, trackType, schoolCode, station, isValidation, validated, allFilesValidated, color, qty, customSchools, batches, spk, onOpenModal }) {
  const colorMap = { green: 'border-l-green-500', red: 'border-l-red-500', yellow: 'border-l-yellow-500', purple: 'border-l-purple-500' }
  const stationLabels = { validasi: 'Validasi File', cetak_isi: 'Cetak Isi', potong_isi: 'Potong Isi', susun: 'Susun', cetak_cover: 'Cetak Cover', qc_cover: 'QC Cover', laminasi: 'Laminasi', cetak_sisipan: 'Cetak Sisipan', qc_sisipan: 'QC Sisipan', done: 'Selesai' }

  // Color scheme for different station types:
  // - Validasi: Blue (bg-blue-600)
  // - Info Cetak: Amber/Yellow (bg-amber-500) 
  // - QC Cetak: Teal (bg-teal-600)
  // - Peruntukan/Susun: Indigo (bg-indigo-600)
  // - QC Cover/Sisipan: Orange (bg-orange-500)
  // Color scheme for different station types:
  // - Validasi: Blue (bg-blue-600)
  // - Info Cetak: Slate (bg-slate-600) - changed from amber to differentiate
  // - QC Cetak: Teal (bg-teal-600)
  // - Peruntukan: Yellow (bg-yellow-500) - UNIQUE yellow
  // - QC Cover/Sisipan: Rose (bg-rose-500) - changed from orange
  // - Laminasi: Pink (bg-pink-600)
  
  const getButton = () => {
    // ISI track
    if (track === 'ISI') {
      if (station === 'validasi') return { label: 'Validasi', modal: 'validation', color: 'bg-blue-600', icon: FileText }
      if (station === 'cetak_isi') return { label: 'Info Cetak', modal: 'infoCetak', data: { type: 'isi', label: 'ISI', schoolCode: null, qty: spk.qty }, color: 'bg-slate-600', icon: Printer }
      if (station === 'potong_isi') return { label: 'QC Cetak & Lanjut', modal: 'qcCetak', data: { type: 'isi', label: 'Cetak Isi' }, color: 'bg-teal-600', icon: Search }
      if (station === 'susun') return { label: 'Pilih Peruntukan & Buat Batch', modal: 'peruntukan', color: 'bg-yellow-500', icon: Layers }
    }
    // COVER tracks
    if (trackType === 'cover' || trackType === 'cover_custom') {
      if (station === 'validasi' && trackType === 'cover_custom' && validated) return { label: 'Lanjut Cetak Cover', modal: 'lanjutCustom', data: { track: 'cover', schoolCode }, color: 'bg-blue-600', icon: CheckCircle }
      if (station === 'cetak_cover') return { label: 'Info Cetak', modal: 'infoCetak', data: { type: trackType, label: track, schoolCode, qty, isReguler: trackType === 'cover' }, color: 'bg-slate-600', icon: Printer }
      if (station === 'qc_cover') return { label: 'QC', modal: 'qcCover', data: { type: trackType, label: track, schoolCode, qty }, color: 'bg-rose-500', icon: Search }
      if (station === 'laminasi') return { label: 'Lanjut', modal: 'laminasi', data: { type: trackType, label: track, schoolCode }, color: 'bg-pink-600', icon: Layers }
    }
    // SISIPAN tracks
    if (trackType === 'sisipan') {
      if (station === 'validasi' && validated) return { label: 'Lanjut Cetak Sisipan', modal: 'lanjutCustom', data: { track: 'sisipan', schoolCode }, color: 'bg-blue-600', icon: CheckCircle }
      if (station === 'cetak_sisipan') return { label: 'Info Cetak', modal: 'infoCetak', data: { type: 'sisipan', label: track, schoolCode, qty, isSisipan: true }, color: 'bg-slate-600', icon: Printer }
      if (station === 'qc_sisipan') return { label: 'QC', modal: 'qcCover', data: { type: 'sisipan', label: track, schoolCode, qty }, color: 'bg-rose-500', icon: Search }
    }
    return null
  }

  const btn = getButton()
  const isCustomAtValidation = station === 'validasi' && (trackType === 'cover_custom' || trackType === 'sisipan')
  const isBlocked = !validated && !isValidation && !isCustomAtValidation
  const showBadges = track === 'ISI' && station === 'susun'
  
  // Check if all peruntukan are complete (for hiding the button)
  const isAllPeruntukanComplete = () => {
    if (station !== 'susun') return false
    const isRegulerComplete = spk.batching_completed || false
    const customSchools = spk.customSchools || []
    const allCustomComplete = customSchools.every(cs => cs.batching_completed)
    return isRegulerComplete && (customSchools.length === 0 || allCustomComplete)
  }

  if (station === 'done') return null
  
  // Hide peruntukan card if all complete
  if (station === 'susun' && isAllPeruntukanComplete()) return null

  // Badge color based on station type
  const getBadgeStyle = () => {
    if (isValidation) return 'bg-blue-100 text-blue-700'
    if (station === 'susun') return 'bg-yellow-100 text-yellow-700'
    if (station?.includes('laminasi')) return 'bg-pink-100 text-pink-700'
    if (station?.includes('qc')) return 'bg-rose-100 text-rose-700'
    if (station?.includes('cetak')) return 'bg-slate-100 text-slate-700'
    return 'bg-gray-100 text-gray-700'
  }

  const getBadgeLabel = () => {
    if (isValidation) return 'VALIDASI'
    if (station === 'susun') return 'SUSUN'
    if (station?.includes('laminasi')) return 'LAMINASI'
    if (station?.includes('qc')) return 'QC'
    if (station?.includes('cetak')) return 'CETAK'
    return station?.toUpperCase()
  }

  // Find print info for this track (vendor + mode)
  const trackTypeKey = track === 'ISI' ? 'isi' : (trackType || 'cover')
  const printInfo = (spk.printInfo || []).find(pi =>
    pi.track_type === trackTypeKey &&
    (schoolCode ? pi.school_code === schoolCode : !pi.school_code)
  )
  const fileLabel = track === 'ISI' ? 'Isi'
    : trackType === 'sisipan' ? 'Sisipan'
    : trackType === 'cover_custom' ? 'Cover Custom'
    : 'Cover'

  return (
    <div className={`bg-white rounded-xl shadow-sm border-l-4 ${colorMap[color] || 'border-l-gray-500'} p-3`}>
      <div className="flex justify-between items-center mb-2 flex-wrap gap-1">
        <div className="flex items-center gap-2">
          {validated ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-yellow-500" />}
          <span className="font-semibold text-sm">{track}</span>
          <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] rounded font-medium border border-indigo-100">📄 {fileLabel}</span>
          <span className="text-gray-500 text-xs">• {stationLabels[station] || station}</span>
        </div>
        <div className="flex gap-1 flex-wrap">
          {showBadges && <>
            <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs rounded cursor-pointer font-medium" onClick={() => onOpenModal('peruntukan')}>PILIH PERUNTUKAN</span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">+CUSTOM</span>
          </>}
          {isBlocked && <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">BLOCKED</span>}
          <span className={`px-2 py-0.5 text-xs rounded ${getBadgeStyle()}`}>
            {getBadgeLabel()}
          </span>
        </div>
      </div>

      {printInfo && (
        <div className="flex items-center gap-2 text-xs mb-2 bg-gray-50 px-2 py-1.5 rounded flex-wrap">
          <span className="text-gray-500">🏭 Vendor:</span>
          {printInfo.vendor_type === 'internal' ? (
            <>
              <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded font-medium">Internal</span>
              {printInfo.print_mode && (
                <span className={`px-1.5 py-0.5 rounded font-medium ${
                  printInfo.print_mode === 'offset'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {printInfo.print_mode === 'offset' ? '🖨️ Offset' : '⚡ Comcolor'}
                </span>
              )}
            </>
          ) : (
            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
              {printInfo.vendor_name || 'Makloon'}
            </span>
          )}
          {printInfo.qty > 0 && <span className="text-gray-500">• {Number(printInfo.qty).toLocaleString()} eks</span>}
          <button
            onClick={() => onOpenModal('editPrintInfo', printInfo)}
            className="ml-auto text-blue-600 hover:text-blue-800 hover:underline text-xs flex items-center gap-1"
            title="Edit Info Cetak"
          >
            ✏️ Edit
          </button>
        </div>
      )}

      {isBlocked && (
        <div className="flex items-center gap-2 text-yellow-700 text-xs mb-2 bg-yellow-50 p-2 rounded">
          <AlertTriangle className="w-3 h-3" />
          File belum divalidasi
        </div>
      )}

      {btn && !isBlocked ? (
        <button onClick={() => onOpenModal(btn.modal, btn.data)} className={`w-full flex items-center justify-center gap-2 py-2.5 ${btn.color} text-white rounded-lg text-sm font-medium hover:opacity-90`}>
          <btn.icon className="w-4 h-4" /> {btn.label}
        </button>
      ) : isBlocked ? (
        <div className="w-full py-2.5 bg-gray-100 rounded-lg text-center text-gray-500 text-sm">
          <Clock className="w-4 h-4 inline mr-1" /> Menunggu
        </div>
      ) : null}
    </div>
  )
}

// ============================================
// VALIDATION MODAL (with nested QC Modal)
// ============================================
function ValidationModal({ spk: initialSpk, onClose, onSuccess, onRefresh }) {
  const [spk, setSpk] = useState(initialSpk)
  const [selectedFile, setSelectedFile] = useState(null)
  const [showQCModal, setShowQCModal] = useState(false)
  
  const files = spk.files || []
  const customSchools = spk.customSchools || []
  const getFiles = (type, code) => files.filter(f => f.file_type === type && (code ? f.school_code === code : !f.school_code))
  const getValidated = (type, code) => getFiles(type, code).filter(f => f.status === 'validated').length

  // Per-track validation status
  const isiValidated = getFiles('isi').length > 0 && getFiles('isi').every(f => f.status === 'validated')
  const coverValidated = getFiles('cover').length > 0 && getFiles('cover').every(f => f.status === 'validated')
  const customStatus = customSchools.map(cs => {
    const cFiles = getFiles('cover_custom', cs.school_code)
    const cValid = cFiles.length > 0 && cFiles.every(f => f.status === 'validated')
    let sValid = true
    if (cs.custom_type === 'cover_sisipan') {
      const sFiles = getFiles('sisipan', cs.school_code)
      sValid = sFiles.length > 0 && sFiles.every(f => f.status === 'validated')
    }
    return { ...cs, coverValid: cValid, sisipanValid: sValid }
  })

  // Minimal: ISI dan Cover Reguler harus validated untuk bisa lanjut
  const canProceed = isiValidated && coverValidated
  const allValidated = canProceed && customStatus.every(cs => cs.coverValid && cs.sisipanValid)
  const hasUnvalidatedCustom = customStatus.some(cs => !cs.coverValid || !cs.sisipanValid)

  const handleOpenQC = (file) => {
    setSelectedFile(file)
    setShowQCModal(true)
  }

  const handleQCSuccess = async () => {
    setShowQCModal(false)
    setSelectedFile(null)
    // Refresh SPK data to get updated file status
    try {
      const response = await spkService.getById(spk.id)
      if (response.success) {
        setSpk(response.data.spk)
      }
    } catch (e) {
      console.error('Error refreshing SPK:', e)
    }
  }

  const handleFinish = async () => {
    try {
      // ISI & Cover Reguler selalu dilanjutkan (sudah pasti validated di sini)
      await spkService.updateStation(spk.id, { track: 'isi', nextStation: 'cetak_isi' })

      // Custom: hanya lanjutkan track yang sudah validated
      for (const cs of customStatus) {
        if (cs.coverValid) {
          await spkService.updateCustomStation(spk.id, { schoolCode: cs.school_code, track: 'cover', nextStation: 'cetak_cover' })
        }
        if (cs.custom_type === 'cover_sisipan' && cs.sisipanValid) {
          await spkService.updateCustomStation(spk.id, { schoolCode: cs.school_code, track: 'sisipan', nextStation: 'cetak_sisipan' })
        }
      }
      onSuccess()
    } catch (e) {
      alert('Error: ' + (e.response?.data?.message || e.message))
    }
  }

  return (
    <>
      <Modal title="Validasi File" onClose={onClose}>
        <div className="space-y-3">
          <div className="bg-yellow-50 rounded-lg p-2">
            <p className="font-bold text-yellow-800 text-sm">{spk.judul}</p>
            <p className="text-yellow-700 text-xs">{spk.id} • {spk.qty} eks</p>
            {customSchools.length > 0 && <p className="text-yellow-600 text-xs">Custom: {customSchools.map(c => c.school_code).join(', ')}</p>}
          </div>

          <div className="bg-gray-50 rounded-lg p-2">
            <h4 className="font-medium text-gray-700 text-xs mb-1">Checklist File:</h4>
            <div className="grid grid-cols-2 gap-1 text-xs">
              <CheckItem label="File ISI (1)" ok={getValidated('isi') > 0} />
              <CheckItem label="Cover Reguler (1)" ok={getValidated('cover') > 0} />
              {customSchools.length > 0 && <CheckItem label={`Cover Custom (${customSchools.length})`} ok={customSchools.every(cs => getValidated('cover_custom', cs.school_code) > 0)} />}
              {customSchools.some(c => c.custom_type === 'cover_sisipan') && <CheckItem label={`Sisipan (${customSchools.filter(c => c.custom_type === 'cover_sisipan').length})`} ok={customSchools.filter(c => c.custom_type === 'cover_sisipan').every(cs => getValidated('sisipan', cs.school_code) > 0)} />}
            </div>
          </div>

          {files.length === 0 ? (
            <div className="bg-yellow-50 rounded p-4 text-center">
              <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-yellow-700 text-sm font-medium">Belum ada file</p>
              <p className="text-yellow-600 text-xs">Upload di Manajemen SPK</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[50vh] overflow-y-auto">
              <FileGroup title="File ISI" files={getFiles('isi')} validated={getValidated('isi')} onQC={handleOpenQC} color="blue" />
              <FileGroup title="Cover Reguler" files={getFiles('cover')} validated={getValidated('cover')} onQC={handleOpenQC} color="red" />
              {customSchools.length > 0 && <FileGroup title="Cover Custom" files={customSchools.flatMap(c => getFiles('cover_custom', c.school_code))} validated={customSchools.reduce((s, c) => s + getValidated('cover_custom', c.school_code), 0)} total={customSchools.length} onQC={handleOpenQC} color="yellow" />}
              {customSchools.some(c => c.custom_type === 'cover_sisipan') && <FileGroup title="Sisipan" files={customSchools.filter(c => c.custom_type === 'cover_sisipan').flatMap(c => getFiles('sisipan', c.school_code))} validated={customSchools.filter(c => c.custom_type === 'cover_sisipan').reduce((s, c) => s + getValidated('sisipan', c.school_code), 0)} total={customSchools.filter(c => c.custom_type === 'cover_sisipan').length} onQC={handleOpenQC} color="purple" />}
            </div>
          )}

          {hasUnvalidatedCustom && canProceed && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
              <p className="text-amber-700 text-xs flex items-center gap-1 font-medium">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" /> File custom belum lengkap
              </p>
              <p className="text-amber-600 text-xs mt-0.5">
                ISI & Cover Reguler bisa lanjut cetak duluan. File custom yang sudah validated juga ikut dilanjutkan. Sisanya bisa dilanjutkan nanti setelah file validated.
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t">
            <button onClick={handleFinish} disabled={!canProceed} className={`flex-1 py-2.5 rounded-lg text-sm font-medium ${canProceed ? (allValidated ? 'bg-blue-600 text-white' : 'bg-amber-500 text-white') : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>
              <CheckCircle className="w-4 h-4 inline mr-1" /> {allValidated ? 'Selesai & Lanjut Cetak' : 'Lanjut Cetak (Parsial)'}
            </button>
            <button onClick={onClose} className="flex-1 py-2.5 border rounded-lg text-sm">Batal</button>
          </div>
          {!canProceed && <p className="text-yellow-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> File ISI dan Cover Reguler harus divalidasi dulu</p>}
        </div>
      </Modal>

      {/* Nested QC Modal */}
      {showQCModal && selectedFile && (
        <QCFileModal 
          file={selectedFile} 
          onClose={() => { setShowQCModal(false); setSelectedFile(null) }} 
          onSuccess={handleQCSuccess} 
        />
      )}
    </>
  )
}

function CheckItem({ label, ok }) {
  return (
    <div className="flex items-center gap-1">
      {ok ? <CheckCircle className="w-3 h-3 text-green-500" /> : <FileText className="w-3 h-3 text-gray-400" />}
      <span>{label}</span>
    </div>
  )
}

function FileGroup({ title, files, validated, total, onQC, color }) {
  const t = total || files.length || 1
  const borderColors = { blue: 'border-l-blue-500', red: 'border-l-red-500', yellow: 'border-l-yellow-500', purple: 'border-l-purple-500' }
  return (
    <div className={`border-l-4 ${borderColors[color]} pl-2`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-medium text-gray-700 text-xs">{title}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${validated >= t ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{validated}/{t}</span>
      </div>
      {files.length > 0 ? files.map((f, i) => <FileRow key={f.id || i} file={f} onQC={onQC} />) : <p className="text-gray-400 text-xs italic">Belum ada</p>}
    </div>
  )
}

function FileRow({ file, onQC }) {
  const [downloading, setDownloading] = useState(false)
  const handleDownload = async () => {
    setDownloading(true)
    try {
      const res = await fileService.download(file.id)
      const blob = new Blob([res.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.filename
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e) { alert('Gagal download') }
    setDownloading(false)
  }
  return (
    <div className={`rounded p-2 mb-1 ${file.status === 'validated' ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-blue-600 text-xs font-medium truncate max-w-[180px]">{file.filename}</p>
          <p className="text-[10px] text-gray-500">{file.file_size ? `${Math.round(file.file_size/1024)}KB` : ''} {file.pages ? `• ${file.pages} hal` : ''} {file.school_code ? `• ${file.school_code}` : ''}</p>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${file.status === 'validated' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{file.status?.toUpperCase()}</span>
      </div>
      <div className="flex gap-1 mt-1">
        <button onClick={handleDownload} disabled={downloading} className="px-2 py-1 bg-blue-600 text-white text-[10px] rounded">{downloading ? '...' : 'Download'}</button>
        {file.status !== 'validated' && <button onClick={() => onQC(file)} className="px-2 py-1 border border-blue-600 text-blue-600 text-[10px] rounded">QC File</button>}
        {file.status === 'validated' && <span className="text-green-600 text-[10px] flex items-center gap-1"><CheckCircle className="w-3 h-3" />Valid</span>}
      </div>
    </div>
  )
}

// ============================================
// QC FILE MODAL
// ============================================
function QCFileModal({ file, onClose, onSuccess }) {
  const checklists = {
    isi: ['Kesesuaian Judul', 'Halaman Lengkap', 'Footer Sesuai', 'Gambar Sesuai', 'Link Image Benar', 'Nama Penulis'],
    cover: ['Judul Depan & Samping', 'Nama Penulis', 'Logo-logo', 'Tulisan Judul', 'Ukuran File', 'Font'],
    cover_custom: ['Judul Depan & Samping', 'Nama Penulis', 'Logo (+ sekolah)', 'Tulisan Judul', 'Ukuran File', 'Font', 'Kode Sekolah'],
    sisipan: ['Konten Sesuai', 'Jumlah Halaman', 'Ukuran Sesuai', 'Posisi Halaman'],
  }
  const items = checklists[file.file_type] || checklists.isi
  const [checks, setChecks] = useState({})
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const allChecked = items.every((_, i) => checks[i] !== undefined)
  const allOK = items.every((_, i) => checks[i] === true)

  const handleSave = async (status) => {
    setSaving(true)
    try {
      await fileService.validate(file.id, { status, validationData: { checks, notes } })
      onSuccess()
    } catch (e) { alert('Error: ' + e.message) }
    setSaving(false)
  }

  return (
    <Modal title={`QC File ${file.file_type?.toUpperCase()}`} onClose={onClose} zIndex={60}>
      <div className="space-y-3">
        <div className="bg-blue-50 rounded p-2">
          <p className="text-blue-700 text-sm font-medium truncate">{file.filename}</p>
          <p className="text-blue-600 text-xs">{file.file_size ? `${Math.round(file.file_size/1024)}KB` : ''} {file.pages ? `• ${file.pages} hal` : ''}</p>
        </div>
        <div className="space-y-1">
          {items.map((item, i) => (
            <div key={i} className="flex justify-between items-center py-1.5 border-b">
              <span className="text-sm text-gray-700">{item}</span>
              <div className="flex gap-1">
                <button onClick={() => setChecks(p => ({...p, [i]: true}))} className={`px-2 py-0.5 rounded text-xs ${checks[i] === true ? 'bg-green-500 text-white' : 'bg-gray-100'}`}>✓ OK</button>
                <button onClick={() => setChecks(p => ({...p, [i]: false}))} className={`px-2 py-0.5 rounded text-xs ${checks[i] === false ? 'bg-red-500 text-white' : 'bg-gray-100'}`}>✗ NO</button>
              </div>
            </div>
          ))}
        </div>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full border rounded p-2 text-sm" rows={2} placeholder="Catatan (opsional)" />
        <div className="flex gap-2">
          <button onClick={() => handleSave('validated')} disabled={!allChecked || !allOK || saving} className={`flex-1 py-2 rounded text-sm font-medium ${allChecked && allOK ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>Valid</button>
          <button onClick={() => handleSave('rejected')} disabled={!allChecked || saving} className={`flex-1 py-2 rounded text-sm font-medium ${allChecked ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>Tolak</button>
          <button onClick={onClose} className="px-4 py-2 border rounded text-sm">Batal</button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================
// INFO CETAK MODAL
// ============================================
function InfoCetakModal({ spk, track, vendors, onClose, onSuccess }) {
  const customSchools = spk.customSchools || []
  const [vendor, setVendor] = useState('')
  const [printMode, setPrintMode] = useState('offset')
  const [qty, setQty] = useState(track.qty || spk.qty)
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0])
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const isInternal = vendor === 'internal'
  const showModeOptions = isInternal && (track.type === 'isi' || track.isSisipan)

  // Calculate qty for cover reguler
  useEffect(() => {
    if (track.isReguler) {
      const customQty = customSchools.reduce((s, c) => s + (c.qty || 0), 0)
      setQty(spk.qty - customQty)
    }
  }, [track, spk, customSchools])

  const handleSave = async () => {
    if (!vendor) return alert('Pilih vendor')
    setSaving(true)
    try {
      await spkService.addPrintInfo(spk.id, {
        trackType: track.type,
        schoolCode: track.schoolCode,
        vendor: vendor === 'internal' ? null : vendor,
        printMode: isInternal ? printMode : null,
        qty,
        startDate,
        notes
      })
      // Move to next station
      const nextStation = track.type === 'isi' ? 'potong_isi' : track.type === 'sisipan' ? 'qc_sisipan' : 'qc_cover'
      if (track.type === 'isi') {
        await spkService.updateStation(spk.id, { track: 'isi', nextStation })
      } else if (track.schoolCode) {
        await spkService.updateCustomStation(spk.id, { schoolCode: track.schoolCode, track: track.type === 'sisipan' ? 'sisipan' : 'cover', nextStation })
      } else {
        await spkService.updateStation(spk.id, { track: 'cover', nextStation })
      }
      onSuccess()
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)) }
    setSaving(false)
  }

  return (
    <Modal title={`Info Cetak ${track.label}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-purple-50 rounded p-2">
          <p className="font-bold text-purple-800 text-sm">{spk.judul}</p>
          <p className="text-purple-700 text-xs">{track.label} • SPK Total: {spk.qty} eks</p>
          {track.isReguler && <p className="text-yellow-600 text-xs">⚠ Dikurangi {customSchools.length} sekolah custom ({customSchools.reduce((s,c)=>s+(c.qty||0),0)} eks)</p>}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Vendor Cetak *</label>
          <select value={vendor} onChange={e => setVendor(e.target.value)} className="w-full border rounded p-2 mt-1 text-sm">
            <option value="">Pilih Vendor</option>
            <option value="internal">Internal (Mesin Sendiri)</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>

        {showModeOptions && (
          <div>
            <label className="text-sm font-medium text-gray-700">Mode Cetak Internal *</label>
            <div className="flex gap-2 mt-1">
              <label className={`flex-1 p-2 border rounded cursor-pointer text-center text-sm ${printMode === 'offset' ? 'border-blue-500 bg-blue-50' : ''}`}>
                <input type="radio" name="mode" value="offset" checked={printMode === 'offset'} onChange={() => setPrintMode('offset')} className="hidden" />
                <div className="font-medium">🏭 Offset</div>
                <div className="text-xs text-gray-500">Melalui proses Susun</div>
              </label>
              <label className={`flex-1 p-2 border rounded cursor-pointer text-center text-sm ${printMode === 'comcolor' ? 'border-blue-500 bg-blue-50' : ''}`}>
                <input type="radio" name="mode" value="comcolor" checked={printMode === 'comcolor'} onChange={() => setPrintMode('comcolor')} className="hidden" />
                <div className="font-medium">🖨 Comcolor</div>
                <div className="text-xs text-gray-500">Digital print</div>
              </label>
            </div>
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700">Jumlah Eksemplar *</label>
          <input type="number" value={qty} onChange={e => setQty(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Tanggal Mulai *</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border rounded p-2 mt-1 text-sm" />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Catatan</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full border rounded p-2 mt-1 text-sm" rows={2} placeholder="Opsional" />
        </div>

        <div className="flex gap-2 pt-2 border-t">
          <button onClick={handleSave} disabled={!vendor || saving} className={`flex-1 py-2.5 rounded text-sm font-medium ${vendor ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>
            <CheckCircle className="w-4 h-4 inline mr-1" /> Simpan & Lanjut
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 border rounded text-sm">Batal</button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================
// QC CETAK MODAL (for ISI)
// ============================================
function QCCetakModal({ spk, track, onClose, onSuccess }) {
  const printInfo = (spk.printInfo || []).find(p => p.track_type === 'isi')
  const [jumlahAwal] = useState(printInfo?.qty || spk.qty)
  const [jumlahReject, setJumlahReject] = useState(0)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const jumlahAkhir = jumlahAwal - jumlahReject
  const rejectPercent = jumlahAwal > 0 ? ((jumlahReject / jumlahAwal) * 100).toFixed(1) : 0

  const handleSave = async () => {
    setSaving(true)
    try {
      await spkService.addQCResult(spk.id, {
        qcType: 'cetak_isi',
        jumlahAwal,
        jumlahReject,
        jumlahAkhir,
        status: 'passed',
        catatan: notes
      })
      await spkService.updateStation(spk.id, { track: 'isi', nextStation: 'susun' })
      onSuccess()
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)) }
    setSaving(false)
  }

  return (
    <Modal title="QC Hasil Cetak Isi" onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-yellow-50 rounded p-2">
          <p className="font-bold text-yellow-800 text-sm">{spk.judul}</p>
          <p className="text-yellow-700 text-xs">{spk.qty} eksemplar • Mode: {printInfo?.print_mode || 'offset'}</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded p-2">
          <p className="text-blue-700 text-sm font-medium">⚠ QC Hasil Cetak sebelum lanjut ke Susun</p>
          <p className="text-blue-600 text-xs">Setelah QC ini, Anda bisa memproses buku secara bertahap (batch) dari Susun sampai Gudang</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Jumlah Awal (dari cetak)</label>
          <input type="number" value={jumlahAwal} className="w-full border rounded p-2 mt-1 text-sm bg-gray-50" readOnly />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Jumlah Reject</label>
          <input type="number" value={jumlahReject} onChange={e => setJumlahReject(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" min={0} max={jumlahAwal} />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Jumlah Akhir (akan menjadi qty untuk batching)</label>
          <div className="w-full border rounded p-2 mt-1 text-sm bg-green-50 text-green-700 font-bold">{jumlahAkhir}</div>
        </div>

        {jumlahReject > 0 && (
          <div className="bg-yellow-100 border border-yellow-300 rounded p-2 text-yellow-800 text-sm">
            ⚠ {jumlahReject} eksemplar reject ({rejectPercent}%)
          </div>
        )}

        <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full border rounded p-2 text-sm" rows={2} placeholder="Catatan tambahan" />

        <div className="flex gap-2 pt-2 border-t">
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-blue-600 text-white rounded text-sm font-medium">
            <CheckCircle className="w-4 h-4 inline mr-1" /> Simpan & Lanjut ke Susun
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 border rounded text-sm">Batal</button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================
// PERUNTUKAN MODAL
// ============================================
function PeruntukanModal({ spk, onClose, onSuccess }) {
  const customSchools = spk.customSchools || []
  const batches = spk.batches || []
  const qcData = spk.qc_data || {}

  const totalFromQC = qcData.cetak_isi?.jumlahAkhir || spk.qty
  const customTotalQty = customSchools.reduce((s, c) => s + (c.qty || 0), 0)
  const regulerTotal = totalFromQC - customTotalQty

  const [selectedType, setSelectedType] = useState('reguler')
  const [selectedSchool, setSelectedSchool] = useState(customSchools[0]?.school_code || '')
  const [jumlahBatch, setJumlahBatch] = useState(0)
  const [jumlahReject, setJumlahReject] = useState(0)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmType, setConfirmType] = useState(null) // 'complete' or 'abandon'
  const [showEditBatchModal, setShowEditBatchModal] = useState(false)
  const [editingBatch, setEditingBatch] = useState(null)

  const openEditBatchModal = (batch) => {
    setEditingBatch(batch)
    setShowEditBatchModal(true)
  }

  const handleEditBatchSuccess = () => {
    setShowEditBatchModal(false)
    setEditingBatch(null)
    onSuccess()
  }

  const getTypeBatches = (type, code = null) => batches.filter(b => b.batch_type === type && (code ? b.school_code === code : !b.school_code))
  const getBatchedQty = (type, code = null) => getTypeBatches(type, code).reduce((s, b) => s + (b.qty_original || b.qty), 0)
  
  // Check if batching is complete for each type
  const isRegulerComplete = spk.batching_completed || false
  const isCustomComplete = (code) => {
    const cs = customSchools.find(c => c.school_code === code)
    return cs?.batching_completed || false
  }
  
  const regulerBatched = getBatchedQty('reguler')
  const regulerRemaining = regulerTotal - regulerBatched

  const getCustomRemaining = (code) => {
    const cs = customSchools.find(c => c.school_code === code)
    return (cs?.qty || 0) - getBatchedQty('custom', code)
  }

  const currentRemaining = selectedType === 'reguler' ? regulerRemaining : getCustomRemaining(selectedSchool)
  const currentBatches = selectedType === 'reguler' ? getTypeBatches('reguler') : getTypeBatches('custom', selectedSchool)
  const currentTotal = selectedType === 'reguler' ? regulerTotal : (customSchools.find(c => c.school_code === selectedSchool)?.qty || 0)
  const nextBatchNum = currentBatches.length + 1
  const jumlahAkhir = jumlahBatch - jumlahReject
  const sisaSetelahBatch = currentRemaining - jumlahBatch
  
  // Check if current selection is complete
  const isCurrentComplete = selectedType === 'reguler' ? isRegulerComplete : isCustomComplete(selectedSchool)

  // Check if ALL peruntukan are complete
  const allPeruntukanComplete = isRegulerComplete && customSchools.every(cs => isCustomComplete(cs.school_code))

  // Reset jumlahBatch when selection changes
  useEffect(() => {
    setJumlahBatch(0)
    setJumlahReject(0)
  }, [selectedType, selectedSchool])

  const handleCreateBatch = async () => {
    if (jumlahBatch <= 0 || jumlahBatch > currentRemaining) return alert('Jumlah tidak valid')
    setSaving(true)
    try {
      await spkService.createBatch(spk.id, {
        batchType: selectedType,
        schoolCode: selectedType === 'custom' ? selectedSchool : null,
        qty: jumlahAkhir,
        qtyOriginal: jumlahBatch,
        qcData: { jumlahAwal: jumlahBatch, jumlahReject, jumlahAkhir, notes }
      })
      onSuccess()
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)) }
    setSaving(false)
  }

  const openConfirmModal = (type) => {
    setConfirmType(type)
    setShowConfirmModal(true)
  }

  const handleConfirmFinish = async () => {
    setSaving(true)
    try {
      await spkService.markBatchingComplete(spk.id, {
        batchType: selectedType,
        schoolCode: selectedType === 'custom' ? selectedSchool : null,
        skippedQty: currentRemaining
      })
      setShowConfirmModal(false)
      onSuccess()
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)) }
    setSaving(false)
  }

  const currentLabel = selectedType === 'reguler' ? '📦 Reguler' : `🏫 ${selectedSchool}`

  return (
    <>
      <Modal title="QC Halaman - Pilih Peruntukan" onClose={onClose} wide>
        <div className="space-y-3">
          <div className="bg-amber-50 rounded p-2">
            <p className="font-bold text-amber-800 text-sm">{spk.judul}</p>
            <p className="text-amber-700 text-xs">{totalFromQC} total • Mode: {qcData.cetak_isi?.print_mode || 'offset'}</p>
          </div>

          {/* Show all complete message */}
          {allPeruntukanComplete ? (
            <div className="bg-green-50 border border-green-200 rounded p-4 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-green-700 font-bold text-base">Semua Peruntukan Selesai! 🎉</p>
              <p className="text-green-600 text-sm mt-1">Reguler dan semua sekolah custom sudah diproses</p>
              <div className="mt-3 text-xs text-gray-600">
                <p>📦 Reguler: {getTypeBatches('reguler').length} batch</p>
                {customSchools.map(cs => (
                  <p key={cs.school_code}>🏫 {cs.school_code}: {getTypeBatches('custom', cs.school_code).length} batch</p>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700">Pilih Peruntukan Batch *</label>
                <div className="space-y-2 mt-2">
                  {/* Reguler Option */}
                  <label className={`flex items-center gap-2 p-2 border rounded cursor-pointer ${
                    isRegulerComplete ? 'bg-gray-100 border-gray-300 opacity-60' :
                    selectedType === 'reguler' ? 'border-green-500 bg-green-50' : ''
                  }`}>
                    <input 
                      type="radio" 
                      checked={selectedType === 'reguler'} 
                      onChange={() => setSelectedType('reguler')} 
                      disabled={isRegulerComplete}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">📦 Reguler</span>
                        {isRegulerComplete && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded">✓ SELESAI</span>}
                      </div>
                      <span className="text-xs text-gray-500">Sisa: {regulerRemaining} eks • Total: {regulerTotal} eks • {getTypeBatches('reguler').length} batch</span>
                    </div>
                  </label>
                  
                  {/* Custom Schools Options */}
                  {customSchools.map(cs => {
                    const csComplete = isCustomComplete(cs.school_code)
                    const csRemaining = getCustomRemaining(cs.school_code)
                    return (
                      <label key={cs.school_code} className={`flex items-center gap-2 p-2 border rounded cursor-pointer ${
                        csComplete ? 'bg-gray-100 border-gray-300 opacity-60' :
                        selectedType === 'custom' && selectedSchool === cs.school_code ? 'border-yellow-500 bg-yellow-50' : ''
                      }`}>
                        <input 
                          type="radio" 
                          checked={selectedType === 'custom' && selectedSchool === cs.school_code} 
                          onChange={() => { setSelectedType('custom'); setSelectedSchool(cs.school_code) }} 
                          disabled={csComplete}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">🏫 {cs.school_name || cs.school_code}</span>
                            {csComplete && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded">✓ SELESAI</span>}
                          </div>
                          <span className="text-xs text-gray-500">Sisa: {csRemaining} eks • Total: {cs.qty} eks • {getTypeBatches('custom', cs.school_code).length} batch</span>
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>

              {/* Show completed message if current selection is complete */}
              {isCurrentComplete && (
                <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-1" />
                  <p className="text-green-700 font-medium text-sm">{selectedType === 'reguler' ? 'Reguler' : selectedSchool} sudah selesai</p>
                  <p className="text-green-600 text-xs">Pilih peruntukan lain atau tutup modal</p>
                </div>
              )}

              {/* Only show form if not complete */}
              {!isCurrentComplete && (
                <>
                  {currentBatches.length > 0 && (
                    <div className="bg-amber-50 rounded p-2">
                      <p className="font-medium text-amber-800 text-sm">🎯 Batch {selectedType === 'reguler' ? 'Reguler' : selectedSchool}: {currentBatches.length}</p>
                      {currentBatches.map(b => {
                        const batchQcData = b.qc_data?.susun || {}
                        return (
                          <div key={b.id} className="flex justify-between items-center text-xs text-amber-700 py-1 border-b border-amber-200 last:border-0">
                            <div>
                              <span className="font-medium">{b.batch_number}</span>
                              <span className="ml-2">{b.qty} eks → {b.station}</span>
                              {batchQcData.jumlahReject > 0 && <span className="ml-1 text-red-500">(Reject: {batchQcData.jumlahReject})</span>}
                            </div>
                            <button
                              onClick={() => openEditBatchModal(b)}
                              className="px-2 py-0.5 bg-yellow-500 text-white text-[10px] rounded hover:bg-yellow-600"
                            >
                              Edit
                            </button>
                          </div>
                        )
                      })}
                      <p className="text-xs text-amber-600 mt-1">Total sudah diproses: {getBatchedQty(selectedType, selectedType === 'custom' ? selectedSchool : null)} eks</p>
                    </div>
                  )}

                  <div className={`rounded p-2 ${currentRemaining === 0 ? 'bg-green-50' : 'bg-blue-50'}`}>
                    <p className={`font-medium text-sm ${currentRemaining === 0 ? 'text-green-800' : 'text-blue-800'}`}>
                      📊 Sisa {currentLabel}: {currentRemaining} eks
                      {currentRemaining === 0 && ' ✓'}
                    </p>
                  </div>

                  {currentRemaining > 0 && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Jumlah Batch {nextBatchNum} {currentLabel} (yang sudah selesai susun) *</label>
                        <input type="number" value={jumlahBatch} onChange={e => setJumlahBatch(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" placeholder={`Max: ${currentRemaining}`} max={currentRemaining} min={0} />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700">Jumlah Reject</label>
                        <input type="number" value={jumlahReject} onChange={e => setJumlahReject(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" min={0} max={jumlahBatch} />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700">Jumlah Akhir ke Binding</label>
                        <div className="w-full border rounded p-2 mt-1 text-sm bg-green-50 text-green-700 font-bold">{jumlahAkhir}</div>
                      </div>

                      {jumlahBatch > 0 && sisaSetelahBatch > 0 && (
                        <p className="text-xs text-gray-500">📝 Setelah batch ini, sisa {sisaSetelahBatch} eks untuk {selectedType === 'reguler' ? 'Reguler' : selectedSchool}</p>
                      )}

                      <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full border rounded p-2 text-sm" rows={2} placeholder="Catatan tambahan" />
                    </>
                  )}

                  <div className="flex flex-col gap-2 pt-2 border-t">
                    {currentRemaining > 0 && (
                      <button onClick={handleCreateBatch} disabled={jumlahBatch <= 0 || saving} className={`w-full py-2.5 rounded text-sm font-medium ${jumlahBatch > 0 ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>
                        🎯 Buat Batch {nextBatchNum} {currentLabel} ({jumlahAkhir} eks)
                      </button>
                    )}
                    <button 
                      onClick={() => openConfirmModal(currentRemaining === 0 ? 'complete' : 'abandon')} 
                      disabled={saving} 
                      className={`w-full py-2.5 rounded text-sm font-medium ${currentRemaining === 0 ? 'bg-green-600 text-white' : 'bg-orange-500 text-white'}`}
                    >
                      {currentRemaining === 0 
                        ? `✅ Selesai ${currentLabel}`
                        : `⚠️ Abaikan Sisa & Selesai ${currentLabel} (${currentRemaining} eks)`
                      }
                    </button>
                  </div>
                </>
              )}
            </>
          )}
          
          <button onClick={onClose} className="w-full py-2.5 border rounded text-sm">Tutup</button>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ConfirmPeruntukanModal
          type={confirmType}
          label={currentLabel}
          remaining={currentRemaining}
          batchCount={currentBatches.length}
          totalProcessed={getBatchedQty(selectedType, selectedType === 'custom' ? selectedSchool : null)}
          saving={saving}
          onConfirm={handleConfirmFinish}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}

      {/* Edit Batch Modal */}
      {showEditBatchModal && editingBatch && (
        <EditBatchSusunModal
          spk={spk}
          batch={editingBatch}
          onClose={() => { setShowEditBatchModal(false); setEditingBatch(null) }}
          onSuccess={handleEditBatchSuccess}
        />
      )}
    </>
  )
}

// ============================================
// EDIT BATCH SUSUN MODAL
// ============================================
function EditBatchSusunModal({ spk, batch, onClose, onSuccess }) {
  const existingSusun = batch.qc_data?.susun || {}
  const [jumlahAwal, setJumlahAwal] = useState(existingSusun.jumlahAwal || batch.qty_original || batch.qty || 0)
  const [jumlahReject, setJumlahReject] = useState(existingSusun.jumlahReject || 0)
  const [notes, setNotes] = useState(existingSusun.notes || '')
  const [saving, setSaving] = useState(false)

  const jumlahAkhir = jumlahAwal - jumlahReject

  const handleSave = async () => {
    setSaving(true)
    try {
      await spkService.updateBatchQC(spk.id, batch.id, {
        qcType: 'susun',
        jumlahAwal,
        jumlahReject,
        jumlahAkhir,
        notes
      })
      onSuccess()
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)) }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70]">
      <div className="bg-white w-full max-w-sm mx-4 rounded-xl shadow-xl overflow-hidden">
        <div className="p-4 bg-amber-500 text-white">
          <h3 className="font-bold text-lg">Edit QC Susun - {batch.batch_number}</h3>
          <p className="text-sm opacity-90">{batch.batch_type === 'custom' ? `🏫 ${batch.school_code}` : '📦 Reguler'}</p>
        </div>

        <div className="p-4 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-2">
            <p className="text-amber-700 text-xs">Data awal: {batch.qty_original || batch.qty} eks</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Jumlah Awal (dari susun)</label>
            <input type="number" value={jumlahAwal} onChange={e => setJumlahAwal(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Jumlah Reject</label>
            <input type="number" value={jumlahReject} onChange={e => setJumlahReject(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" min={0} />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Jumlah Akhir ke Binding</label>
            <div className="w-full border rounded p-2 mt-1 text-sm bg-green-50 text-green-700 font-bold">{jumlahAkhir}</div>
          </div>

          <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full border rounded p-2 text-sm" rows={2} placeholder="Catatan tambahan" />
        </div>

        <div className="p-4 bg-gray-50 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100">
            Batal
          </button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600">
            {saving ? 'Menyimpan...' : 'Update Data'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================
// CONFIRM PERUNTUKAN MODAL
// ============================================
function ConfirmPeruntukanModal({ type, label, remaining, batchCount, totalProcessed, saving, onConfirm, onCancel }) {
  const isAbandon = type === 'abandon'
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70]">
      <div className="bg-white w-full max-w-sm mx-4 rounded-xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className={`p-4 ${isAbandon ? 'bg-orange-500' : 'bg-green-500'} text-white`}>
          <div className="flex items-center gap-3">
            {isAbandon ? (
              <AlertTriangle className="w-8 h-8" />
            ) : (
              <CheckCircle className="w-8 h-8" />
            )}
            <div>
              <h3 className="font-bold text-lg">
                {isAbandon ? 'Konfirmasi Abaikan Sisa' : 'Konfirmasi Selesai'}
              </h3>
              <p className="text-sm opacity-90">{label}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {isAbandon ? (
            <>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-orange-800 font-medium text-sm">⚠️ Perhatian!</p>
                <p className="text-orange-700 text-sm mt-1">
                  Anda akan mengabaikan <strong>{remaining} eksemplar</strong> yang belum diproses untuk {label}.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Total Batch:</span>
                  <span className="font-medium">{batchCount} batch</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Sudah Diproses:</span>
                  <span className="font-medium text-green-600">{totalProcessed} eks</span>
                </div>
                <div className="flex justify-between text-orange-600 border-t mt-2 pt-2">
                  <span>Akan Diabaikan:</span>
                  <span className="font-bold">{remaining} eks</span>
                </div>
              </div>
              <p className="text-gray-500 text-xs">
                Tindakan ini tidak dapat dibatalkan. Sisa eksemplar tidak akan diproses lebih lanjut.
              </p>
            </>
          ) : (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-green-800 font-medium text-sm">✅ Semua Selesai!</p>
                <p className="text-green-700 text-sm mt-1">
                  Semua eksemplar untuk {label} sudah diproses.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Total Batch:</span>
                  <span className="font-medium">{batchCount} batch</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Total Diproses:</span>
                  <span className="font-bold">{totalProcessed} eks</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 flex gap-2">
          <button 
            onClick={onCancel} 
            disabled={saving}
            className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Batal
          </button>
          <button 
            onClick={onConfirm} 
            disabled={saving}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium text-white ${
              isAbandon ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600'
            } ${saving ? 'opacity-50' : ''}`}
          >
            {saving ? 'Menyimpan...' : (isAbandon ? 'Ya, Abaikan' : 'Ya, Selesai')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================
// QC COVER MODAL
// ============================================
function QCCoverModal({ spk, track, vendors, onClose, onSuccess }) {
  const printInfo = (spk.printInfo || []).find(p => p.track_type === track.type && (track.schoolCode ? p.school_code === track.schoolCode : !p.school_code))
  const vendorName = printInfo?.vendor_id ? vendors.find(v => v.id === printInfo.vendor_id)?.name || 'Vendor' : 'Internal'
  const [jumlahAwal] = useState(printInfo?.qty || track.qty || 0)
  const [jumlahReject, setJumlahReject] = useState(0)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const jumlahAkhir = jumlahAwal - jumlahReject

  const handleSave = async () => {
    setSaving(true)
    try {
      const qcType = track.type === 'sisipan' ? 'qc_sisipan' : 'qc_cover'
      await spkService.addQCResult(spk.id, {
        qcType,
        schoolCode: track.schoolCode,
        jumlahAwal,
        jumlahReject,
        jumlahAkhir,
        status: 'passed',
        catatan: notes
      })
      const nextStation = track.type === 'sisipan' ? 'done' : 'laminasi'
      if (track.schoolCode) {
        await spkService.updateCustomStation(spk.id, { schoolCode: track.schoolCode, track: track.type === 'sisipan' ? 'sisipan' : 'cover', nextStation })
      } else {
        await spkService.updateStation(spk.id, { track: 'cover', nextStation })
      }
      onSuccess()
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)) }
    setSaving(false)
  }

  return (
    <Modal title={`QC ${track.type === 'sisipan' ? 'Sisipan' : 'Cover'}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-pink-50 rounded p-2">
          <p className="font-bold text-pink-800 text-sm">{spk.judul}</p>
          <p className="text-pink-700 text-xs">{track.label} • {jumlahAwal} eksemplar</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Nama Vendor (dari Info Cetak)</label>
          <input type="text" value={vendorName} className="w-full border rounded p-2 mt-1 text-sm bg-gray-50" readOnly />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Jumlah Awal (dari Info Cetak)</label>
          <input type="number" value={jumlahAwal} className="w-full border rounded p-2 mt-1 text-sm bg-gray-50" readOnly />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Jumlah Reject</label>
          <input type="number" value={jumlahReject} onChange={e => setJumlahReject(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" min={0} />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Jumlah Akhir</label>
          <div className="w-full border rounded p-2 mt-1 text-sm bg-green-50 text-green-700 font-bold">{jumlahAkhir}</div>
        </div>

        <div className="flex gap-2 pt-2 border-t">
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-blue-600 text-white rounded text-sm font-medium">
            <CheckCircle className="w-4 h-4 inline mr-1" /> Simpan & Lanjut
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 border rounded text-sm">Batal</button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================
// LAMINASI MODAL
// ============================================
function LaminasiModal({ spk, track, operators = [], onClose, onSuccess }) {
  const qcKey = track.schoolCode ? `qc_cover_${track.schoolCode}` : 'qc_cover'
  const qcResult = spk.qc_data?.[qcKey] || {}
  const laminasiKey = track.schoolCode ? `laminasi_${track.schoolCode}` : 'laminasi'
  const existingLaminasi = spk.qc_data?.[laminasiKey] || null
  const existingQcResult = (spk.qcResults || []).find(q => q.qc_type === 'laminasi' && (track.schoolCode ? q.school_code === track.schoolCode : !q.school_code))

  const [isEditMode, setIsEditMode] = useState(false)
  const [jumlahAwal, setJumlahAwal] = useState(existingLaminasi?.jumlahAwal || qcResult.jumlahAkhir || 0)
  const [jumlahReject, setJumlahReject] = useState(existingLaminasi?.jumlahReject || 0)
  const [notes, setNotes] = useState(existingLaminasi?.catatan || '')
  const [operatorName, setOperatorName] = useState(existingLaminasi?.operatorName || '')
  const [saving, setSaving] = useState(false)

  const jumlahAkhir = jumlahAwal - jumlahReject
  const rejectPercent = jumlahAwal > 0 ? ((jumlahReject / jumlahAwal) * 100).toFixed(1) : 0

  const handleSave = async () => {
    if (!operatorName.trim()) return alert('Nama operator harus diisi')
    setSaving(true)
    try {
      if (isEditMode && existingQcResult) {
        await spkService.updateQCResult(spk.id, existingQcResult.id, {
          jumlahAwal,
          jumlahReject,
          jumlahAkhir,
          catatan: notes,
          operatorName
        })
      } else {
        await spkService.addQCResult(spk.id, {
          qcType: 'laminasi',
          schoolCode: track.schoolCode,
          jumlahAwal,
          jumlahReject,
          jumlahAkhir,
          status: 'passed',
          catatan: notes,
          operatorName
        })
        if (track.schoolCode) {
          await spkService.updateCustomStation(spk.id, { schoolCode: track.schoolCode, track: 'cover', nextStation: 'done' })
        } else {
          await spkService.updateStation(spk.id, { track: 'cover', nextStation: 'done' })
        }
      }
      onSuccess()
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)) }
    setSaving(false)
  }

  return (
    <Modal title={isEditMode ? "Edit QC Laminasi" : "QC Laminasi"} onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-pink-50 rounded p-2">
          <p className="font-bold text-pink-800 text-sm">{spk.judul}</p>
          <p className="text-pink-700 text-xs">{spk.qty} eksemplar</p>
        </div>

        {existingLaminasi && !isEditMode && (
          <div className="bg-green-50 border border-green-200 rounded p-3">
            <div className="flex justify-between items-start mb-2">
              <p className="text-green-700 text-sm font-medium">Data QC Laminasi Sebelumnya:</p>
              <button onClick={() => setIsEditMode(true)} className="px-2 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600">
                Edit
              </button>
            </div>
            <div className="text-xs text-green-600 space-y-1">
              <p>Jumlah Awal: {existingLaminasi.jumlahAwal}</p>
              <p>Jumlah Reject: {existingLaminasi.jumlahReject}</p>
              <p>Jumlah Akhir: {existingLaminasi.jumlahAkhir}</p>
              {existingLaminasi.operatorName && <p>Operator: {existingLaminasi.operatorName}</p>}
              {existingLaminasi.catatan && <p>Catatan: {existingLaminasi.catatan}</p>}
            </div>
          </div>
        )}

        {(!existingLaminasi || isEditMode) && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded p-2">
              <p className="text-blue-700 text-sm font-medium">⚠ QC Laminasi sebelum cover masuk ke binding</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Nama Operator *</label>
              {operators.length > 0 ? (
                <select value={operatorName} onChange={e => setOperatorName(e.target.value)} className="w-full border rounded p-2 mt-1 text-sm">
                  <option value="">-- Pilih Operator --</option>
                  {operators.map(op => (
                    <option key={op.id} value={op.name}>{op.name}</option>
                  ))}
                </select>
              ) : (
                <input type="text" value={operatorName} onChange={e => setOperatorName(e.target.value)} className="w-full border rounded p-2 mt-1 text-sm" placeholder="Masukkan nama operator" />
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Jumlah Awal (dari QC Cover)</label>
              <input type="number" value={jumlahAwal} onChange={e => setJumlahAwal(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" />
              <p className="text-xs text-gray-500 mt-1">✓ Default dari hasil QC Cover: {qcResult.jumlahAkhir || 0} eks</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Jumlah Reject</label>
              <input type="number" value={jumlahReject} onChange={e => setJumlahReject(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" min={0} />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Jumlah Akhir</label>
              <div className="w-full border rounded p-2 mt-1 text-sm bg-green-50 text-green-700 font-bold">{jumlahAkhir}</div>
            </div>

            {jumlahReject > 0 && (
              <div className="bg-yellow-100 border border-yellow-300 rounded p-2 text-yellow-800 text-sm">
                ⚠ {jumlahReject} cover reject ({rejectPercent}%)
              </div>
            )}

            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full border rounded p-2 text-sm" rows={2} placeholder="Catatan tambahan" />

            <div className="flex gap-2 pt-2 border-t">
              <button onClick={handleSave} disabled={saving || !operatorName.trim()} className={`flex-1 py-2.5 rounded text-sm font-medium ${operatorName.trim() ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>
                <CheckCircle className="w-4 h-4 inline mr-1" /> {isEditMode ? 'Update Data' : 'Simpan & Cover Siap'}
              </button>
              {isEditMode && (
                <button onClick={() => setIsEditMode(false)} className="flex-1 py-2.5 border rounded text-sm">Batal Edit</button>
              )}
              <button onClick={onClose} className="flex-1 py-2.5 border rounded text-sm">Tutup</button>
            </div>
          </>
        )}

        {existingLaminasi && !isEditMode && (
          <button onClick={onClose} className="w-full py-2.5 border rounded text-sm">Tutup</button>
        )}
      </div>
    </Modal>
  )
}

// ============================================
// QC BINDING MODAL
// ============================================
function QCBindingModal({ spk, batch, operators = [], onClose, onSuccess }) {
  const existingBinding = batch.qc_data?.binding || null
  const [isEditMode, setIsEditMode] = useState(false)
  const [jumlahAwal, setJumlahAwal] = useState(existingBinding?.jumlahAwal || batch.qty || 0)
  const [jumlahReject, setJumlahReject] = useState(existingBinding?.jumlahReject || 0)
  const [notes, setNotes] = useState(existingBinding?.notes || '')
  const [operatorName, setOperatorName] = useState(existingBinding?.operatorName || '')
  const [saving, setSaving] = useState(false)

  const jumlahAkhir = jumlahAwal - jumlahReject
  const rejectPercent = jumlahAwal > 0 ? ((jumlahReject / jumlahAwal) * 100).toFixed(1) : 0

  const handleSave = async () => {
    if (!operatorName.trim()) return alert('Nama operator harus diisi')
    setSaving(true)
    try {
      if (isEditMode && existingBinding) {
        await spkService.updateBatchQC(spk.id, batch.id, {
          qcType: 'binding',
          jumlahAwal,
          jumlahReject,
          jumlahAkhir,
          notes,
          operatorName
        })
      } else {
        await spkService.updateBatchStation(spk.id, batch.id, {
          nextStation: 'potong_sisir',
          qcResult: { qcType: 'binding', jumlahAwal, jumlahReject, jumlahAkhir, notes, operatorName }
        })
      }
      onSuccess()
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)) }
    setSaving(false)
  }

  return (
    <Modal title={isEditMode ? `Edit QC Binding - ${batch.batch_number}` : `QC Binding - ${batch.batch_number}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-green-50 rounded p-2">
          <p className="font-bold text-green-800 text-sm">{spk.judul}</p>
          <p className="text-green-700 text-xs">📦 {batch.batch_number} • {batch.qty} eksemplar</p>
        </div>

        {existingBinding && !isEditMode && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
            <div className="flex justify-between items-start mb-2">
              <p className="text-emerald-700 text-sm font-medium">Data QC Binding Sebelumnya:</p>
              <button onClick={() => setIsEditMode(true)} className="px-2 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600">
                Edit
              </button>
            </div>
            <div className="text-xs text-emerald-600 space-y-1">
              <p>Jumlah Awal: {existingBinding.jumlahAwal}</p>
              <p>Jumlah Reject: {existingBinding.jumlahReject}</p>
              <p>Jumlah Akhir: {existingBinding.jumlahAkhir}</p>
              {existingBinding.operatorName && <p>Operator: {existingBinding.operatorName}</p>}
              {existingBinding.notes && <p>Catatan: {existingBinding.notes}</p>}
            </div>
          </div>
        )}

        {(!existingBinding || isEditMode) && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded p-2">
              <p className="text-blue-700 text-sm font-medium">⚠ QC Binding sebelum lanjut ke Potong Sisir</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Nama Operator *</label>
              {operators.length > 0 ? (
                <select value={operatorName} onChange={e => setOperatorName(e.target.value)} className="w-full border rounded p-2 mt-1 text-sm">
                  <option value="">-- Pilih Operator --</option>
                  {operators.map(op => (
                    <option key={op.id} value={op.name}>{op.name}</option>
                  ))}
                </select>
              ) : (
                <input type="text" value={operatorName} onChange={e => setOperatorName(e.target.value)} className="w-full border rounded p-2 mt-1 text-sm" placeholder="Masukkan nama operator" />
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Jumlah Awal (dari QC Halaman)</label>
              <input type="number" value={jumlahAwal} onChange={e => setJumlahAwal(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" />
              <p className="text-xs text-gray-500 mt-1">✓ Default dari QC Halaman: {batch.qty} eks</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Jumlah Reject</label>
              <input type="number" value={jumlahReject} onChange={e => setJumlahReject(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" min={0} />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Jumlah Akhir</label>
              <div className="w-full border rounded p-2 mt-1 text-sm bg-green-50 text-green-700 font-bold">{jumlahAkhir}</div>
            </div>

            {jumlahReject > 0 && (
              <div className="bg-yellow-100 border border-yellow-300 rounded p-2 text-yellow-800 text-sm">
                ⚠ {jumlahReject} buku reject ({rejectPercent}%)
              </div>
            )}

            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full border rounded p-2 text-sm" rows={2} placeholder="Catatan tambahan" />

            <div className="flex gap-2 pt-2 border-t">
              <button onClick={handleSave} disabled={saving || !operatorName.trim()} className={`flex-1 py-2.5 rounded text-sm font-medium ${operatorName.trim() ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>
                <CheckCircle className="w-4 h-4 inline mr-1" /> {isEditMode ? 'Update Data' : 'Simpan & Lanjut ke Potong'}
              </button>
              {isEditMode && (
                <button onClick={() => setIsEditMode(false)} className="flex-1 py-2.5 border rounded text-sm">Batal Edit</button>
              )}
              <button onClick={onClose} className="flex-1 py-2.5 border rounded text-sm">Tutup</button>
            </div>
          </>
        )}

        {existingBinding && !isEditMode && (
          <button onClick={onClose} className="w-full py-2.5 border rounded text-sm">Tutup</button>
        )}
      </div>
    </Modal>
  )
}

// ============================================
// QC POTONG MODAL
// ============================================
function QCPotongModal({ spk, batch, operators = [], onClose, onSuccess }) {
  const qcBinding = batch.qc_data?.binding || {}
  const existingPotong = batch.qc_data?.potong || null
  const [isEditMode, setIsEditMode] = useState(false)
  const [jumlahAwal, setJumlahAwal] = useState(existingPotong?.jumlahAwal || qcBinding.jumlahAkhir || batch.qty || 0)
  const [jumlahReject, setJumlahReject] = useState(existingPotong?.jumlahReject || 0)
  const [notes, setNotes] = useState(existingPotong?.notes || '')
  const [operatorName, setOperatorName] = useState(existingPotong?.operatorName || '')
  const [saving, setSaving] = useState(false)

  const jumlahAkhir = jumlahAwal - jumlahReject

  const handleSave = async () => {
    if (!operatorName.trim()) return alert('Nama operator harus diisi')
    setSaving(true)
    try {
      if (isEditMode && existingPotong) {
        await spkService.updateBatchQC(spk.id, batch.id, {
          qcType: 'potong',
          jumlahAwal,
          jumlahReject,
          jumlahAkhir,
          notes,
          operatorName
        })
      } else {
        await spkService.updateBatchStation(spk.id, batch.id, {
          nextStation: 'qc_potong',
          qcResult: { qcType: 'potong', jumlahAwal, jumlahReject, jumlahAkhir, notes, operatorName }
        })
      }
      onSuccess()
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)) }
    setSaving(false)
  }

  return (
    <Modal title={isEditMode ? `Edit QC Potong Sisir - ${batch.batch_number}` : `QC Potong Sisir - ${batch.batch_number}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-blue-50 rounded p-2">
          <p className="font-bold text-blue-800 text-sm">{spk.judul}</p>
          <p className="text-blue-700 text-xs">📦 {batch.batch_number} • {batch.qty} eksemplar</p>
        </div>

        {existingPotong && !isEditMode && (
          <div className="bg-cyan-50 border border-cyan-200 rounded p-3">
            <div className="flex justify-between items-start mb-2">
              <p className="text-cyan-700 text-sm font-medium">Data QC Potong Sebelumnya:</p>
              <button onClick={() => setIsEditMode(true)} className="px-2 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600">
                Edit
              </button>
            </div>
            <div className="text-xs text-cyan-600 space-y-1">
              <p>Jumlah Awal: {existingPotong.jumlahAwal}</p>
              <p>Jumlah Reject: {existingPotong.jumlahReject}</p>
              <p>Jumlah Akhir: {existingPotong.jumlahAkhir}</p>
              {existingPotong.operatorName && <p>Operator: {existingPotong.operatorName}</p>}
              {existingPotong.notes && <p>Catatan: {existingPotong.notes}</p>}
            </div>
          </div>
        )}

        {(!existingPotong || isEditMode) && (
          <>
            <div>
              <label className="text-sm font-medium text-gray-700">Nama Operator *</label>
              {operators.length > 0 ? (
                <select value={operatorName} onChange={e => setOperatorName(e.target.value)} className="w-full border rounded p-2 mt-1 text-sm">
                  <option value="">-- Pilih Operator --</option>
                  {operators.map(op => (
                    <option key={op.id} value={op.name}>{op.name}</option>
                  ))}
                </select>
              ) : (
                <input type="text" value={operatorName} onChange={e => setOperatorName(e.target.value)} className="w-full border rounded p-2 mt-1 text-sm" placeholder="Masukkan nama operator" />
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Jumlah Awal (dari QC Binding)</label>
              <input type="number" value={jumlahAwal} onChange={e => setJumlahAwal(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" />
              <p className="text-xs text-gray-500 mt-1">✓ Default dari QC Binding: {qcBinding.jumlahAkhir || batch.qty} eks</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Jumlah Reject</label>
              <input type="number" value={jumlahReject} onChange={e => setJumlahReject(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" min={0} />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Jumlah Akhir</label>
              <div className="w-full border rounded p-2 mt-1 text-sm bg-green-50 text-green-700 font-bold">{jumlahAkhir}</div>
            </div>

            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full border rounded p-2 text-sm" rows={2} placeholder="Catatan tambahan" />

            <div className="flex gap-2 pt-2 border-t">
              <button onClick={handleSave} disabled={saving || !operatorName.trim()} className={`flex-1 py-2.5 rounded text-sm font-medium ${operatorName.trim() ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>
                <CheckCircle className="w-4 h-4 inline mr-1" /> {isEditMode ? 'Update Data' : 'Simpan & Lanjut'}
              </button>
              {isEditMode && (
                <button onClick={() => setIsEditMode(false)} className="flex-1 py-2.5 border rounded text-sm">Batal Edit</button>
              )}
              <button onClick={onClose} className="flex-1 py-2.5 border rounded text-sm">Tutup</button>
            </div>
          </>
        )}

        {existingPotong && !isEditMode && (
          <button onClick={onClose} className="w-full py-2.5 border rounded text-sm">Tutup</button>
        )}
      </div>
    </Modal>
  )
}

// ============================================
// GUDANG MODAL
// ============================================
function GudangModal({ spk, batch, racks, onClose, onSuccess }) {
  const qcPotong = batch.qc_data?.potong || {}
  const [jumlahMasuk, setJumlahMasuk] = useState(qcPotong.jumlahAkhir || batch.qty || 0)
  const [selectedRack, setSelectedRack] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  // Get selected rack details (id is VARCHAR in database)
  const selectedRackData = racks.find(r => r.id === selectedRack || r.kode === selectedRack)

  const handleSave = async () => {
    if (!selectedRack) return alert('Pilih rak terlebih dahulu')
    setSaving(true)
    try {
      await spkService.addWarehouseEntry(spk.id, {
        batchId: batch.id || null,
        qty: jumlahMasuk || 0,
        rackId: selectedRack || null,
        rackCode: selectedRackData?.kode || selectedRack || null,
        entryDate: new Date().toISOString().split('T')[0],
        notes: notes || ''
      })
      await spkService.updateBatchStation(spk.id, batch.id, { nextStation: 'gudang' })
      onSuccess()
    } catch (e) { alert('Error: ' + (e.response?.data?.message || e.message)) }
    setSaving(false)
  }

  return (
    <Modal title={`Input Gudang - ${batch.batch_number}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-purple-50 rounded p-2">
          <p className="font-bold text-purple-800 text-sm">{spk.judul}</p>
          <p className="text-purple-700 text-xs">📦 {batch.batch_number} • {batch.qty} eksemplar</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Jumlah Masuk Gudang</label>
          <input type="number" value={jumlahMasuk} onChange={e => setJumlahMasuk(Number(e.target.value))} className="w-full border rounded p-2 mt-1 text-sm" />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Pilih Rak *</label>
          <select 
            value={selectedRack} 
            onChange={e => setSelectedRack(e.target.value)} 
            className="w-full border rounded p-2 mt-1 text-sm"
          >
            <option value="">-- Pilih Rak --</option>
            {racks.map(rack => (
              <option key={rack.id || rack.kode} value={rack.id || rack.kode}>
                {rack.kode} - {rack.nama} {rack.lokasi ? `(${rack.lokasi})` : ''} 
                {rack.kapasitas ? ` • Kapasitas: ${rack.kapasitas}` : ''}
              </option>
            ))}
          </select>
          {selectedRackData && (
            <div className="mt-1 p-2 bg-gray-50 rounded text-xs text-gray-600">
              <p><strong>Kode:</strong> {selectedRackData.kode}</p>
              <p><strong>Nama:</strong> {selectedRackData.nama}</p>
              {selectedRackData.lokasi && <p><strong>Lokasi:</strong> {selectedRackData.lokasi}</p>}
              {selectedRackData.kapasitas && <p><strong>Kapasitas:</strong> {selectedRackData.kapasitas}</p>}
              {selectedRackData.used !== undefined && <p><strong>Terpakai:</strong> {selectedRackData.used} dari {selectedRackData.kapasitas}</p>}
            </div>
          )}
          {racks.length === 0 && (
            <p className="text-yellow-600 text-xs mt-1">⚠️ Belum ada rak. Tambahkan di menu Master Rak</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Catatan</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full border rounded p-2 mt-1 text-sm" rows={2} placeholder="Catatan tambahan" />
        </div>

        <div className="flex gap-2 pt-2 border-t">
          <button onClick={handleSave} disabled={saving || !selectedRack} className={`flex-1 py-2.5 rounded text-sm font-medium ${selectedRack ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>
            <Warehouse className="w-4 h-4 inline mr-1" /> Simpan ke Gudang
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 border rounded text-sm">Batal</button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================
// MODAL WRAPPER
// ============================================
// ============================================
// ROLLBACK STATION MODAL (Admin only)
// ============================================
function RollbackModal({ spk, data, onClose, onSuccess }) {
  const [track, setTrack] = useState('')
  const [schoolCode, setSchoolCode] = useState('')
  const [targetStation, setTargetStation] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const customSchools = data?.customSchools || []

  const trackOptions = [
    { value: 'isi', label: 'ISI', current: spk.station_isi },
    { value: 'cover', label: 'COVER (Reguler)', current: spk.station_cover },
    ...customSchools.map(cs => ({
      value: `cover_${cs.school_code}`, label: `COVER (${cs.school_code})`, current: cs.station_cover, schoolCode: cs.school_code, track: 'cover'
    })),
    ...customSchools.filter(c => c.custom_type === 'cover_sisipan').map(cs => ({
      value: `sisipan_${cs.school_code}`, label: `SISIPAN (${cs.school_code})`, current: cs.station_sisipan, schoolCode: cs.school_code, track: 'sisipan'
    })),
  ]

  const stationMap = {
    isi: [
      { id: 'validasi', name: 'Validasi File' },
      { id: 'cetak_isi', name: 'Cetak Isi' },
      { id: 'potong_isi', name: 'Potong Isi' },
      { id: 'susun', name: 'Susun' },
    ],
    cover: [
      { id: 'cetak_cover', name: 'Cetak Cover' },
      { id: 'qc_cover', name: 'QC Cover' },
      { id: 'laminasi', name: 'Laminasi' },
    ],
    sisipan: [
      { id: 'cetak_sisipan', name: 'Cetak Sisipan' },
      { id: 'qc_sisipan', name: 'QC Sisipan' },
    ],
  }

  const selectedTrackOption = trackOptions.find(t => t.value === track)
  const actualTrack = selectedTrackOption?.track || track
  const actualSchoolCode = selectedTrackOption?.schoolCode || ''
  const currentStation = selectedTrackOption?.current || ''
  const availableStations = stationMap[actualTrack] || []

  // Filter: only stations before current
  const currentIndex = currentStation === 'done'
    ? availableStations.length
    : availableStations.findIndex(s => s.id === currentStation)
  const rollbackStations = availableStations.filter((_, i) => i < currentIndex)

  const handleSubmit = async () => {
    if (!track || !targetStation || !reason.trim()) {
      setError('Semua field harus diisi')
      return
    }
    setLoading(true)
    setError('')
    try {
      await spkService.rollbackStation(spk.id, {
        track: actualTrack,
        schoolCode: actualSchoolCode || undefined,
        targetStation,
        reason: reason.trim(),
      })
      onSuccess()
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal rollback stasiun')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal title="Kembalikan Stasiun" onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-700">
          <AlertTriangle className="w-4 h-4 inline mr-1" />
          Fitur ini akan mengembalikan stasiun ke posisi sebelumnya. Pastikan alasan sudah jelas.
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Pilih Track</label>
          <select
            value={track}
            onChange={e => { setTrack(e.target.value); setTargetStation('') }}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">-- Pilih Track --</option>
            {trackOptions.map(t => (
              <option key={t.value} value={t.value} disabled={t.current === availableStations?.[0]?.id || (!t.current)}>
                {t.label} — saat ini: {t.current === 'done' ? 'Selesai' : t.current || 'belum mulai'}
              </option>
            ))}
          </select>
        </div>

        {track && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Stasiun Tujuan</label>
            {rollbackStations.length === 0 ? (
              <p className="text-xs text-gray-500">Tidak ada stasiun yang bisa di-rollback (sudah di stasiun pertama)</p>
            ) : (
              <select
                value={targetStation}
                onChange={e => setTargetStation(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">-- Pilih Stasiun Tujuan --</option>
                {rollbackStations.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Alasan Rollback *</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Jelaskan alasan mengapa stasiun harus dikembalikan..."
            className="w-full border rounded-lg px-3 py-2 text-sm h-20 resize-none"
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">Batal</button>
          <button
            onClick={handleSubmit}
            disabled={loading || !track || !targetStation || !reason.trim()}
            className="flex-1 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
          >
            {loading ? 'Memproses...' : 'Kembalikan Stasiun'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================
// ROLLBACK BATCH STATION MODAL (Admin only)
// ============================================
function RollbackBatchModal({ spk, batch, onClose, onSuccess }) {
  const [targetStation, setTargetStation] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const mainStations = [
    { id: 'binding', name: 'Binding' },
    { id: 'potong_sisir', name: 'Potong Sisir' },
    { id: 'qc_potong', name: 'QC Potong' },
    { id: 'gudang', name: 'Gudang' },
  ]

  const currentIndex = batch.station === 'done'
    ? mainStations.length
    : mainStations.findIndex(s => s.id === batch.station)
  const rollbackStations = mainStations.filter((_, i) => i < currentIndex)

  const handleSubmit = async () => {
    if (!targetStation || !reason.trim()) {
      setError('Stasiun tujuan dan alasan harus diisi')
      return
    }
    setLoading(true)
    setError('')
    try {
      await spkService.rollbackBatchStation(spk.id, batch.id, {
        targetStation,
        reason: reason.trim(),
      })
      onSuccess()
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal rollback batch')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal title={`Rollback Batch ${batch.batch_number}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-700">
          <AlertTriangle className="w-4 h-4 inline mr-1" />
          Batch <strong>{batch.batch_number}</strong> saat ini di stasiun <strong>{batch.station === 'done' ? 'Selesai' : batch.station}</strong>.
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Stasiun Tujuan</label>
          {rollbackStations.length === 0 ? (
            <p className="text-xs text-gray-500">Batch sudah di stasiun pertama</p>
          ) : (
            <select
              value={targetStation}
              onChange={e => setTargetStation(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">-- Pilih Stasiun Tujuan --</option>
              {rollbackStations.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Alasan Rollback *</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Jelaskan alasan mengapa batch harus dikembalikan..."
            className="w-full border rounded-lg px-3 py-2 text-sm h-20 resize-none"
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">Batal</button>
          <button
            onClick={handleSubmit}
            disabled={loading || !targetStation || !reason.trim()}
            className="flex-1 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
          >
            {loading ? 'Memproses...' : 'Kembalikan Batch'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function Modal({ title, children, onClose, wide, zIndex = 50 }) {
  return (
    <div className={`fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center`} style={{ zIndex }}>
      <div className={`bg-white w-full ${wide ? 'sm:max-w-lg' : 'sm:max-w-md'} sm:rounded-xl sm:m-4 max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-t-2xl`}>
        <div className="p-3 border-b flex items-center justify-between bg-white sticky top-0">
          <h2 className="font-bold text-base">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-3 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  )
}

// ============================================
// LANJUT CUSTOM MODAL (untuk track custom yang tertinggal di validasi)
// ============================================
function LanjutCustomModal({ spk, data, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false)
  const nextStation = data.track === 'cover' ? 'cetak_cover' : 'cetak_sisipan'
  const label = data.track === 'cover' ? `Cover Custom (${data.schoolCode})` : `Sisipan (${data.schoolCode})`

  const handleConfirm = async () => {
    setSaving(true)
    try {
      await spkService.updateCustomStation(spk.id, {
        schoolCode: data.schoolCode,
        track: data.track,
        nextStation
      })
      onSuccess()
    } catch (e) {
      alert('Error: ' + (e.response?.data?.message || e.message))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Lanjutkan ke Cetak" onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-blue-800 text-sm font-medium">{label}</p>
          <p className="text-blue-600 text-xs mt-1">File sudah divalidasi. Lanjutkan track ini ke stasiun cetak?</p>
        </div>
        <div className="flex gap-2 pt-2">
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4 inline mr-1" /> {saving ? 'Memproses...' : 'Ya, Lanjut Cetak'}
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 border rounded-lg text-sm">Batal</button>
        </div>
      </div>
    </Modal>
  )
}

// ============================================
// EDIT PRINT INFO MODAL
// ============================================
function EditPrintInfoModal({ spk, printInfo, vendors, onClose, onSuccess }) {
  const [vendorId, setVendorId] = useState(printInfo.vendor_id || '')
  const [printMode, setPrintMode] = useState(printInfo.print_mode || '')
  const [qty, setQty] = useState(printInfo.qty || '')
  const [startDate, setStartDate] = useState(printInfo.start_date ? printInfo.start_date.slice(0, 10) : '')
  const [notes, setNotes] = useState(printInfo.notes || '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  const selectedVendor = vendors.find(v => v.id === parseInt(vendorId))
  const isInternal = selectedVendor?.type === 'internal'
  const allowMode = isInternal && (printInfo.track_type === 'isi' || printInfo.track_type === 'sisipan')

  const trackLabel = {
    isi: 'ISI', cover: 'Cover Reguler',
    cover_custom: `Cover Custom${printInfo.school_code ? ` (${printInfo.school_code})` : ''}`,
    sisipan: `Sisipan${printInfo.school_code ? ` (${printInfo.school_code})` : ''}`
  }[printInfo.track_type] || printInfo.track_type

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErr(null)
    if (!qty || parseInt(qty) <= 0) return setErr('Jumlah cetak harus > 0')
    if (!vendorId) return setErr('Vendor wajib diisi')
    if (allowMode && !printMode) return setErr('Mode cetak wajib dipilih untuk vendor internal')

    try {
      setSaving(true)
      const res = await spkService.updatePrintInfo(spk.id, printInfo.id, {
        vendor: parseInt(vendorId),
        printMode: allowMode ? printMode : null,
        qty: parseInt(qty),
        startDate: startDate || null,
        notes: notes || '',
      })
      if (res.success) onSuccess()
    } catch (e) {
      setErr(e.response?.data?.message || 'Gagal menyimpan')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={`✏️ Edit Info Cetak — ${trackLabel}`} onClose={onClose} wide>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="bg-gray-50 p-2 rounded text-xs text-gray-600">
          <p className="font-medium">{spk.judul}</p>
          <p className="font-mono">{spk.id}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
          <select
            value={vendorId}
            onChange={(e) => { setVendorId(e.target.value); setPrintMode('') }}
            required
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="">-- Pilih Vendor --</option>
            {vendors.map(v => (
              <option key={v.id} value={v.id}>{v.name} ({v.type})</option>
            ))}
          </select>
        </div>

        {allowMode && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode Cetak *</label>
            <div className="flex gap-2">
              <label className={`flex-1 flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer ${printMode === 'offset' ? 'border-blue-500 bg-blue-50' : ''}`}>
                <input type="radio" value="offset" checked={printMode === 'offset'} onChange={(e) => setPrintMode(e.target.value)} />
                <span className="text-sm">🖨️ Offset</span>
              </label>
              <label className={`flex-1 flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer ${printMode === 'comcolor' ? 'border-emerald-500 bg-emerald-50' : ''}`}>
                <input type="radio" value="comcolor" checked={printMode === 'comcolor'} onChange={(e) => setPrintMode(e.target.value)} />
                <span className="text-sm">⚡ Comcolor</span>
              </label>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Cetak *</label>
          <input
            type="number" min="1" required
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          />
          <p className="text-xs text-gray-500 mt-1">Sebelumnya: {Number(printInfo.qty || 0).toLocaleString()} eks</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Mulai</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="2"
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="Opsional"
          />
        </div>

        {err && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">{err}</div>}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 border rounded-lg text-sm hover:bg-gray-50">Batal</button>
          <button
            type="submit" disabled={saving}
            className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4 inline mr-1" /> {saving ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default StationUpdatePage
